import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatPrice } from "@/utils/formatPrice";
import type { MultiSelectSlot } from "@/hooks/useMultiSelect";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

interface MultiSelectSummaryBarProps {
  slots: MultiSelectSlot[];
  totalPrice: number;
  onClear: () => void;
  onRemoveSlot: (index: number) => void;
  onBook: () => void;
  maxSlots: number;
}

export default function MultiSelectSummaryBar({
  slots,
  totalPrice,
  onClear,
  onRemoveSlot,
  onBook,
  maxSlots,
}: MultiSelectSummaryBarProps) {
  const { t } = useTranslation("booking");

  if (slots.length === 0) return null;

  return (
    <div
      className="fixed bottom-16 lg:bottom-0 left-0 right-0 z-50 bg-card border-t shadow-lg transition-transform duration-300 ease-out"
      style={{ transform: slots.length > 0 ? "translateY(0)" : "translateY(100%)" }}
    >
      <div className="max-w-3xl mx-auto px-4 py-3 space-y-2">
        {/* Slot count and compact list */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">
              {t("slotsSelectedOfMax", { count: slots.length, max: maxSlots })}
            </p>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {slots.map((slot, i) => (
                <span
                  key={`${slot.courtId}-${slot.startTime}`}
                  className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-[11px] font-medium px-2 py-0.5"
                >
                  {slot.courtName} {format(new Date(slot.startTime), "HH:mm")}
                  <button
                    onClick={() => onRemoveSlot(i)}
                    className="hover:bg-primary/20 rounded-full p-0.5"
                    aria-label={t("removeSlot", { court: slot.courtName, time: format(new Date(slot.startTime), "HH:mm") })}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
          <p className="text-sm font-semibold whitespace-nowrap">
            {t("total")}: {formatPrice(totalPrice)}
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={onClear}>
            {t("clear")}
          </Button>
          <Button size="sm" onClick={onBook}>
            {t("bookTimes", { count: slots.length, label: slots.length === 1 ? t("time") : t("timePlural") })}
          </Button>
        </div>
      </div>
    </div>
  );
}
